import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { Form, Button, Row, Alert, Grid } from '../ui-kit';
import { ProSidebarProvider } from 'react-pro-sidebar';
import { debounce } from 'lodash';

import { isEmpty, isUndefined, isEqual, compact, uniq } from 'lodash';
import { crewTypesI18n } from '../general/CrewTypeLabel';

import { operations as dutyRequestsOperations } from '../../state/modules/duty-requests';
import {
  operations as dutyRequestCandidatesOperations,
  selectors as dutyRequestCandidatesSelectors
} from '../../state/modules/duty-request-candidates';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { operations as skillOperations, selectors as skillSelectors } from '../../state/modules/skills';
import {
  operations as availabilityCodeOperations,
  selectors as availabilityCodeSelectors
} from '../../state/modules/availability-codes';
import { dutyTransactionsSelectors } from '../../state/modules/duty-transactions';
import { operations as contractStatisticsOperations } from '../../state/modules/contract-statistics';

import CandidatesTable from './DutyRequestCandidatesTable';
import DefaultFormGroup from '../../utils/forms/DefaultFormGroup';
import Loader from '../general/Loader';
import PaginatedLoader from '../general/PaginatedLoader';
import SideBarFilters from './DutyRequestCandidatesSideBar';
import PulseLoader from 'react-spinners/PulseLoader';

import { redirectTo } from '../../utils/redirect';
import { unpackFromLocalStorage, addToLocalStorage } from '../../utils/local-storage';
import Fields from './DutyRequestFormFields';

import { usePrevious } from '../../utils/custom-hooks';

const NewDutyRequestForm = ({
  candidates,
  candidatesLoaded,
  candidatesPaginationInfo,
  createNewDutyRequest,
  dutyRequestsById,
  fetchCandidates,
  fetchContractStatistics,
  groupId,
  groups,
  initialStartTime,
  initialEndTime,
  rootGroupId,
  skills,
  skillFilterId,
  fetchSkills,
  fetchAvailabilityCodes,
  availabilityCodes,
  storeDutyRequest,
  updateDutyRequest,
  contractTypeNames,
  initialLoadingComplete
}) => {
  const CREW_TYPES = ['retained', 'wholetime', 'mixed', 'administrative', 'unknown'];

  const defaultInitialFilterState = {
    name: '',
    candidate_type: ['fully_available'],
    crew_type: Object.keys(crewTypesI18n),
    skills: compact([skillFilterId]),
    availableOnly: false,
    memberOf: [groupId.toString()],
    availability_codes: [],
    contractTypeName: 'any'
  };

  // Internal state variables and state setters used to manage internal component
  // state, id keeps track of the current editing duty request id,
  // https://reactjs.org/docs/hooks-reference.html#usestate
  const [id, setId] = useState(null);
  const [contractStatsTimeUnit, setContractStatsTimeUnit] = useState(
    unpackFromLocalStorage('candidate_select_time_unit') || null
  );
  const [pendingPromises, setPendingPromises] = useState({ contracts: [], candidates: [] });
  const [savedSearches, setSavedSearches] = useState({});
  const [candidateFilterState, setCandidateFilterState] = useState({
    name: '',
    candidate_type: ['fully_available'],
    memberOf: [groupId.toString()],
    availableOnly: false,
    contractTypeName: 'any'
  });

  const [processing, setProcessing] = useState(false);

  const [visibilityStatus, setVisibilityStatus] = useState(
    unpackFromLocalStorage('duty_request_candidate_visiblity_status') || {
      skills: true,
      crew_type: true,
      memberOf: true,
      candidate_type: true,
      availability_codes: true,
      balance: false,
      constraints: false
    }
  );

  // supports an array of columnNames or a single one. Reason: when calling this
  // multiple times, a race condition may occur which overwrites the previous
  // setting. Therefore, it's safer to supply an array when hiding/showing
  // multiple columns at once.
  const setVisibilityStatusForColumn = (columnNames, status) => {
    if (typeof columnNames === 'string') {
      columnNames = [columnNames];
    }

    let newVisibilityStatus = visibilityStatus;

    for (const columnName of columnNames) {
      newVisibilityStatus = {
        ...newVisibilityStatus,
        [columnName]: status
      };
    }

    addToLocalStorage('duty_request_candidate_visiblity_status', newVisibilityStatus);

    setVisibilityStatus(newVisibilityStatus);
  };

  const prevCandidateFilterState = usePrevious(candidateFilterState);
  const prevCandidates = usePrevious(candidates);

  // Fetches the initial state of duty functions, availability codes, groups,
  // skills required to build all the form filter options and creates an empty
  // skeleton of the duty request, since we're passing [] as the second argument
  // to useEffect, this effect runs only once
  // https://reactjs.org/docs/hooks-reference.html#useEffect
  useEffect(() => {
    fetchSkills({ params: { group_ids: groupId } });
    fetchAvailabilityCodes({ params: { group_ids: groupId } });

    // If there are any saved searches stored in the local storage, load them.
    const localSavedSearches = unpackFromLocalStorage('candidate_select_saved_searches');
    if (localSavedSearches) setSavedSearches(localSavedSearches);

    createNewDutyRequest({
      start_time: initialStartTime,
      end_time: initialEndTime,
      candidate_user_ids: [],
      owner_id: groupId,
      comments: '',
      resolution_mechanism: 'first_to_respond',
      requested_duty_function_ids: [],
      availability_code_id: null,
      candidate_skill_filter_id: skillFilterId || null,
      type: 'duty_request'
    }).then(dutyRequest => setId(dutyRequest.id));
  }, []);

  const dutyRequest = dutyRequestsById[id];

  // This effect subscribes to the duty request start/end time and the requested
  // duty function ids, so whenever they change and if we already have the
  // list of candidates rendered, we fetch a new list of candidates without the
  // need of clicking the find candidates button.
  useEffect(() => {
    if (dutyRequest && candidatesLoaded) {
      findCandidates();
    }
  }, [dutyRequest?.start_time, dutyRequest?.end_time, dutyRequest?.requested_duty_function_ids]);

  useEffect(() => {
    // Not all filters should trigger a fetch to the API. We first determine
    // if the changed filter should trigger an API call by comparing against a
    // permit list.
    const permittedFilters = ['crew_type', 'memberOf', 'skills', 'availability_codes'];

    // If there is any difference in value for the permittedFilters, perform a
    // new fetch
    const shouldFindCandidates =
      !prevCandidateFilterState ||
      permittedFilters.some(f => !isEqual(candidateFilterState[f], (prevCandidateFilterState || {})[f]));

    if (dutyRequest && shouldFindCandidates) findCandidates();
  }, [candidateFilterState]);

  useEffect(() => {
    // Due to performance constraints, we preload only the current group
    // However the target behaviour is: When all groups are loaded, we
    //need to preload the filter with all options selected
    if (initialLoadingComplete) setCandidateFilterState(defaultInitialFilterState);
  }, [initialLoadingComplete]);

  useEffect(() => {
    addToLocalStorage('candidate_select_time_unit', contractStatsTimeUnit);
    // if contractStatsTimeUnit is null, the columns should be hidden
    setVisibilityStatusForColumn(['balance', 'constraints'], !!contractStatsTimeUnit);

    loadContractStatistics();
  }, [contractStatsTimeUnit]);

  useEffect(() => {
    const previousCandidateIds = (prevCandidates || []).map(c => c.id);
    const newCandidateIds = candidates.map(c => c.id);
    if (isEqual(previousCandidateIds, newCandidateIds)) return;

    loadContractStatistics();
  }, [candidates]);

  const updatePeriod = useCallback(
    period => {
      updateDutyRequest(id, period);
    },
    [id]
  );

  const updateReceivingGroup = useCallback(
    groupId => {
      updateDutyRequest(id, {
        owner_id: groupId
      });
    },
    [id]
  );

  const updateComments = useCallback(
    event => {
      updateDutyRequest(id, { comments: event.target.value });
    },
    [id]
  );

  const updateDutyFunction = useCallback(
    functionIds => {
      // To avaid prop changes leading to re-renders, don't execute
      // if there are no changes
      if (isEqual(dutyRequest.requested_duty_function_ids, functionIds)) return;

      // clear the selection of candidates because they may no
      // longer meet the requirement
      updateDutyRequest(id, {
        requested_duty_function_ids: functionIds,
        candidate_user_ids: [],
        schedule_type: isEmpty(functionIds) ? null : 'standby_duty'
      });
    },
    [id, dutyRequest?.requested_duty_function_ids]
  );

  const updateAvailabilityCode = useCallback(
    availabilityCodes => {
      updateDutyRequest(id, {
        availability_code_id: availabilityCodes[0]
      });
    },
    [id]
  );

  const updateResolutionMechanism = useCallback(
    resolutionMechanism => {
      updateDutyRequest(id, {
        resolution_mechanism: resolutionMechanism[0]
      });
    },
    [id]
  );

  const enqueueFindCandidates = (pendingPromises, params) => {
    for (const promise of pendingPromises.candidates) promise.abortController.abort();

    setPendingPromises({
      ...pendingPromises,
      candidates: [fetchCandidates({ ids: groupId, params, replace: true })]
    });
  };

  const enqueueFindCandidatesDebounced = useCallback(debounce(enqueueFindCandidates, 1000), []);

  const findCandidates = () => {
    const { requested_duty_function_ids } = dutyRequest;
    const start_time = dutyRequest?.start_time || initialStartTime;
    const end_time = dutyRequest?.end_time || initialEndTime;

    let params = { start_time, end_time };

    const addFilterToParams = (filter_key, param_key) => {
      const selected = compact(candidateFilterState[filter_key]);
      if (isUndefined(selected) || isEmpty(selected)) return;
      params = { ...params, [param_key]: selected.filter(Boolean) };
    };

    if (!isEmpty(requested_duty_function_ids)) {
      params = {
        ...params,
        requested_duty_function_ids,
        schedule_type: 'standby_duty'
      };
    }

    addFilterToParams('skills', 'candidate_skill_filter_id');
    addFilterToParams('crew_type', 'crew_types');
    addFilterToParams('memberOf', 'group_ids');
    addFilterToParams('availability_codes', 'availability_code_ids');

    enqueueFindCandidatesDebounced(pendingPromises, params);
  };

  const saveDutyRequest = async event => {
    event.preventDefault();
    setProcessing(true);

    try {
      const headers = { 'Idempotency-Key': uuidv4() };
      const action = await storeDutyRequest(dutyRequest.id, { headers });
      const dutyRequestId = action.payload.data.id;
      const dutyRequestOwnerId = action.payload.data.group_id;
      redirectTo(`/groups/${dutyRequestOwnerId}/schedule/exchanges/duty_requests/${dutyRequestId}`);
    } finally {
      setProcessing(false);
    }
  };

  const loadContractStatistics = () => {
    if (!contractStatsTimeUnit) return;
    // only fetch for candidates for whom we haven't loaded the stats yet
    const userIds = candidatesToShow.filter(c => c.contracts[contractStatsTimeUnit].length === 0).map(c => c.user.id);

    // If there are pending requests, abort them
    for (const promise of pendingPromises.contracts) promise.abortController.abort();

    setPendingPromises({
      ...pendingPromises,
      contracts: userIds.map(userId =>
        fetchContractStatistics({
          ids: userId,
          params: {
            time_unit: contractStatsTimeUnit
          }
        })
      )
    });
  };

  // Utility method to save a filter state in the local storage
  const saveSearch = name => {
    const newSavedSearches = {
      ...savedSearches,
      [name]: candidateFilterState
    };

    // Store in the state to trigger a re-render
    setSavedSearches(newSavedSearches);
    addToLocalStorage('candidate_select_saved_searches', newSavedSearches);
  };

  const applySearch = (name, newFilterState) => {
    setCandidateFilterState(newFilterState);
  };

  const deleteSearch = id => {
    let newSavedSearches = { ...savedSearches };
    delete newSavedSearches[id];
    setSavedSearches(newSavedSearches);
    addToLocalStorage('candidate_select_saved_searches', newSavedSearches);
  };

  const fetchFilterOptions = () => {
    const groupOptions = groups || [];

    const stationOptions = groupOptions.filter(g => g.type === 'station').map(e => ({ id: e.id, name: e.name }));

    const teamOptions = groupOptions.filter(g => g.type === 'team');

    const groupTree = stationOptions.map(station => ({
      ...station,
      teams: teamOptions.filter(t => t.parent_group_id === station.id).map(t => ({ id: t.id, name: t.name }))
    }));

    return {
      candidateTypes: uniq(candidates.map(candidate => candidate.candidate_type)),
      skills,
      availabilityCodes,
      crewTypes: CREW_TYPES,
      groups: groupTree,
      contractTypeNames
    };
  };

  const scopeTypes = {
    station: 'in_station',
    cluster: 'in_cluster_or_region',
    region: 'in_cluster_or_region'
  };

  const candidatesToShow = useMemo(() => {
    // Apply client-side candidate type filter
    let filteredCandidates = candidates;

    filteredCandidates = filteredCandidates.filter(candidate =>
      candidateFilterState.candidate_type.includes(candidate.candidate_type)
    );

    // Apply client-side name filter
    if (candidateFilterState.name)
      filteredCandidates = filteredCandidates.filter(candidate =>
        candidate.fullName.toLowerCase().includes(candidateFilterState.name.toLowerCase())
      );

    const contractTypeName = candidateFilterState.contractTypeName || 'any';
    if (contractTypeName !== 'any')
      filteredCandidates = filteredCandidates.filter(
        candidate =>
          candidate.contracts.shift.some(c => c.contract_type_name === contractTypeName) ||
          candidate.contracts.hour.some(c => c.contract_type_name === contractTypeName)
      );

    if (!candidateFilterState.availableOnly) return filteredCandidates;

    const applyAvailabilityByGroupCriteria = group => {
      // If there filter for memberOf is empty, we don't apply any
      // additional criteria, we will just consider if there is availability
      // for the group
      if (candidateFilterState.memberOf.length === 0) return group.available_time > 0;

      // If we have memberOf ids, we will consider if there is availability
      // for the group AND the group is included in the memerOf ids
      return candidateFilterState.memberOf.includes(group.id.toString()) && group.available_time > 0;
    };

    // Apply client-side availability filter
    return filteredCandidates.filter(candidate =>
      candidate.groups.find(group => applyAvailabilityByGroupCriteria(group))
    );
  }, [
    candidateFilterState.name,
    candidateFilterState.availableOnly,
    candidateFilterState.memberOf.join(','),
    candidateFilterState.candidate_type.join(','),
    candidateFilterState.contractTypeName,
    candidates
  ]);

  const overlappingPeriod = useMemo(
    () => ({
      startTime: dutyRequest?.start_time,
      endTime: dutyRequest?.endTime,
      initialStartTime: initialStartTime,
      initialEndTime: initialEndTime
    }),
    [dutyRequest?.start_time, dutyRequest?.end_time]
  );

  if (!dutyRequest) return <Loader />;

  const group = groups.find(g => g.id === groupId);

  const savingDisabled =
    isEmpty(dutyRequest.candidate_user_ids) || (dutyRequest?.requested_duty_function_ids || []).length === 0;

  return (
    <Row>
      <Grid fluid>
        <Button
          className="saveDutyRequest"
          variant="success"
          size="lg"
          style={{ position: 'fixed', right: '50px', bottom: '50px', zIndex: '999' }}
          disabled={savingDisabled || processing}
          onClick={saveDutyRequest}
        >
          {processing ? <PulseLoader color="#FFF" size="7px" css={{ display: 'inline-block' }} /> : I18n.t('save')}
        </Button>
        <h3>{I18n.t('schedules.exchanges.duty_request.request_params')}</h3>
        <Form>
          <Fields.DutyFunctionSelector
            groupId={groupId}
            selected={dutyRequest.requested_duty_function_ids}
            onChange={updateDutyFunction}
          />
          <Fields.PeriodSelector
            startTime={dutyRequest.start_time}
            endTime={dutyRequest.end_time}
            onChange={updatePeriod}
          />
          <Fields.GroupSelector groupId={rootGroupId} value={dutyRequest.owner_id} onChange={updateReceivingGroup} />
          <Fields.AvailabilityCodeSelector groupId={groupId} onChange={updateAvailabilityCode} />
          <Fields.ResolutionMechanismSelector
            value={dutyRequest.resolution_mechanism}
            onChange={updateResolutionMechanism}
          />
          {group && (
            <DefaultFormGroup>
              <Alert variant={'warning'}>
                {I18n.t(`schedules.exchanges.duty_request.${scopeTypes[group?.type]}`, {
                  group: group?.name
                })}
              </Alert>
            </DefaultFormGroup>
          )}
          <Fields.CommentsField value={dutyRequest.comments} onChange={updateComments} />
          <Fields.OverlappingRequestsTable className="overlappingRequestsTable" period={overlappingPeriod} />
          <br />
          <PaginatedLoader {...candidatesPaginationInfo} />
          {initialLoadingComplete && (
            <ProSidebarProvider>
              <div style={{ display: 'flex' }}>
                <SideBarFilters
                  filterState={candidateFilterState}
                  filterChangeHandler={setCandidateFilterState}
                  filterOptions={fetchFilterOptions()}
                  setContractStatsTimeUnit={setContractStatsTimeUnit}
                  contractStatsTimeUnit={contractStatsTimeUnit}
                  savedSearches={savedSearches}
                  saveSearch={saveSearch}
                  applySearch={applySearch}
                  deleteSearch={deleteSearch}
                  visibilityStatus={visibilityStatus}
                  setVisibilityStatusForColumn={setVisibilityStatusForColumn}
                />
                <main style={{ width: '100%' }}>
                  <CandidatesTable
                    candidates={candidatesToShow}
                    groups={groups}
                    timeUnit={contractStatsTimeUnit}
                    dutyRequestId={dutyRequest.id}
                    updateDutyRequest={updateDutyRequest}
                    selectedCandidates={dutyRequest.candidate_user_ids}
                    visibilityStatus={visibilityStatus}
                    selectedContractTypeName={candidateFilterState.contractTypeName}
                  />
                </main>
              </div>
            </ProSidebarProvider>
          )}
        </Form>
      </Grid>
    </Row>
  );
};

const mapStateToProps = state => ({
  candidates: dutyRequestCandidatesSelectors.dutyRequestCandidatesAugmented(state),
  candidatesLoaded: dutyRequestCandidatesSelectors.initialLoadComplete(state),
  candidatesPaginationInfo: dutyRequestCandidatesSelectors.paginationInfo(state),
  contractStatisticsLoading: state.contractStatistics.loading,
  dutyRequestsById: dutyTransactionsSelectors.augmentedDutyRequestsById(state),
  groups: groupSelectors.values(state),
  skills: skillSelectors.values(state),
  availabilityCodes: availabilityCodeSelectors.values(state),
  contractTypeNames: dutyRequestCandidatesSelectors.loadedContractTypeNames(state),
  initialLoadingComplete:
    groupSelectors.initialLoadComplete(state) &&
    skillSelectors.initialLoadComplete(state) &&
    availabilityCodeSelectors.initialLoadComplete(state)
});

const mapDispatchToProps = {
  createNewDutyRequest: dutyRequestsOperations.createNew,
  fetchCandidates: dutyRequestCandidatesOperations.fetch,
  fetchContractStatistics: contractStatisticsOperations.fetch,
  fetchSkills: skillOperations.fetch,
  fetchAvailabilityCodes: availabilityCodeOperations.fetch,
  updateDutyRequest: dutyRequestsOperations.updateFields,
  storeDutyRequest: dutyRequestsOperations.storeInBackend
};

export default connect(mapStateToProps, mapDispatchToProps)(NewDutyRequestForm);
